// Google Fonts helpers — no API key required.
// We ship a curated "Suggested" list + a large bundled "popular" list that can be
// searched client-side. Any selected font is loaded on demand via the Google Fonts
// CSS endpoint (which needs no key). font-weight is synthesized by the browser where
// a family lacks a given weight, so we request the family without a weight axis to
// avoid 400 errors on single-weight display faces.

export const CURATED_FONTS = [
  // Horror / atmospheric
  { name: "Rusted Plastic", group: "Horror / Atmospheric" },
  { name: "Cinzel", group: "Horror / Atmospheric" },
  { name: "Cinzel Decorative", group: "Horror / Atmospheric" },
  { name: "Crimson Text", group: "Horror / Atmospheric" },
  { name: "Special Elite", group: "Horror / Atmospheric" },
  { name: "Creepster", group: "Horror / Atmospheric" },
  { name: "Nosifer", group: "Horror / Atmospheric" },
  { name: "Butcherman", group: "Horror / Atmospheric" },
  { name: "Uncial Antiqua", group: "Horror / Atmospheric" },
  { name: "MedievalSharp", group: "Horror / Atmospheric" },
  // Clean / readable
  { name: "Inter", group: "Clean / Readable" },
  { name: "Raleway", group: "Clean / Readable" },
  { name: "Oswald", group: "Clean / Readable" },
  { name: "Lato", group: "Clean / Readable" },
  { name: "Montserrat", group: "Clean / Readable" },
  { name: "EB Garamond", group: "Clean / Readable" },
  { name: "Playfair Display", group: "Clean / Readable" },
  { name: "Libre Baskerville", group: "Clean / Readable" },
  { name: "Source Sans 3", group: "Clean / Readable" },
  { name: "Bebas Neue", group: "Clean / Readable" },
  { name: "DM Sans", group: "Clean / Readable" },
];

// A broad bundled list of popular Google Fonts for the "All Fonts" search (no API key).
export const POPULAR_FONTS = [
  "Inter", "Roboto", "Open Sans", "Lato", "Montserrat", "Poppins", "Raleway", "Oswald",
  "Merriweather", "Nunito", "Nunito Sans", "Work Sans", "DM Sans", "DM Serif Display",
  "Playfair Display", "Source Sans 3", "Source Serif 4", "PT Sans", "PT Serif", "Rubik",
  "Quicksand", "Mukta", "Karla", "Barlow", "Barlow Condensed", "Heebo", "Titillium Web",
  "Josefin Sans", "Fjalla One", "Bebas Neue", "Anton", "Archivo", "Archivo Black",
  "Manrope", "Mulish", "Cabin", "Crimson Text", "Crimson Pro", "Libre Baskerville",
  "Libre Franklin", "EB Garamond", "Cormorant", "Cormorant Garamond", "Spectral",
  "Lora", "Bitter", "Domine", "Zilla Slab", "Roboto Slab", "Slabo 27px", "Arvo",
  "Cardo", "Vollkorn", "Frank Ruhl Libre", "Noto Serif", "Noto Sans", "Figtree",
  "Outfit", "Sora", "Space Grotesk", "Space Mono", "JetBrains Mono", "IBM Plex Sans",
  "IBM Plex Serif", "IBM Plex Mono", "Fira Sans", "Fira Code", "Cinzel", "Cinzel Decorative",
  "Special Elite", "Creepster", "Nosifer", "Butcherman", "Eater", "Frijole", "Metal Mania",
  "Pirata One", "Henny Penny", "Griffy", "Uncial Antiqua", "MedievalSharp", "Almendra",
  "UnifrakturCook", "UnifrakturMaguntia", "Pinyon Script", "Tangerine", "Great Vibes",
  "Dancing Script", "Sacramento", "Satisfy", "Pacifico", "Lobster", "Caveat", "Kalam",
  "Shadows Into Light", "Permanent Marker", "Amatic SC", "Bangers", "Fredoka", "Baloo 2",
  "Comfortaa", "Righteous", "Teko", "Abril Fatface", "Alfa Slab One", "Bungee", "Monoton",
  "Press Start 2P", "VT323", "Major Mono Display", "Syne", "Unbounded", "Bricolage Grotesque",
  "Schibsted Grotesk", "Hanken Grotesk", "Albert Sans", "Onest", "Geist", "Instrument Serif",
  "Fraunces", "Newsreader", "Petrona", "Gentium Book Plus", "Marcellus", "Cormorant Infant",
  "Italiana", "Forum", "Yeseva One", "Philosopher", "Prata", "Bodoni Moda", "Sorts Mill Goudy",
  "Old Standard TT", "Rozha One", "Suranna", "Trirong", "Yrsa", "Faustina", "Literata",
  "Besley", "Piazzolla", "Hepta Slab", "Crete Round", "Aleo", "Bree Serif", "Kanit",
  "Exo 2", "Saira", "Saira Condensed", "Chakra Petch", "Orbitron", "Audiowide", "Russo One",
  "Staatliches", "Bowlby One", "Passion One", "Concert One", "Patua One", "Squada One",
  "Changa One", "Paytone One", "Alegreya", "Alegreya Sans", "Asap", "Hind", "Catamaran",
  "Maven Pro", "Varela Round", "Signika", "Questrial", "Jost", "Lexend", "Red Hat Display",
  "Be Vietnam Pro", "Plus Jakarta Sans", "Epilogue", "Sen", "Urbanist", "Gabarito",
].filter((v, i, a) => a.indexOf(v) === i).sort();

const loaded = new Set();

// Self-hosted fonts (not on Google Fonts) — loaded via @font-face in index.css.
// loadGoogleFont() skips these so it never hits the Google CSS endpoint for them.
export const SELF_HOSTED_FONTS = new Set(["Rusted Plastic"]);

export function loadGoogleFont(name) {
  if (!name || typeof document === "undefined") return;
  if (SELF_HOSTED_FONTS.has(name)) return;
  if (loaded.has(name)) return;
  loaded.add(name);
  const id = "gf-" + name.replace(/\s+/g, "-").toLowerCase();
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${name.replace(/\s+/g, "+")}&display=swap`;
  document.head.appendChild(link);
}

// Build a CSS font-family stack with a sensible fallback for the given family.
export function fontStack(name) {
  if (!name) return null;
  const serifish = /(serif|cinzel|crimson|garamond|playfair|baskerville|lora|spectral|cardo|cormorant|merriweather|bitter|slab|antiqua|fraktur|medieval|almendra|prata|marcellus|forum|italiana|newsreader|fraunces|literata)/i;
  const mono = /(mono|code|elite|2p|vt323)/i;
  let fallback = "sans-serif";
  if (mono.test(name)) fallback = "monospace";
  else if (serifish.test(name)) fallback = "serif";
  return `'${name}', ${fallback}`;
}
